// src/learn/FaqSchema.tsx
import React, { useMemo } from 'react';

export type FaqItem = {
  q: string;
  a: string;
};

function cleanText(s: string) {
  return String(s || '')
    .replace(/\s+/g, ' ')
    .trim();
}

export default function FaqSchema({
  items,
  title = 'FAQ',
  maxItems = 10,
}: {
  items: FaqItem[];
  title?: string;
  maxItems?: number;
}) {
  const safeItems = useMemo(() => {
    const arr = Array.isArray(items) ? items : [];
    return arr
      .map((it) => ({
        q: cleanText(it?.q),
        a: cleanText(it?.a),
      }))
      .filter((it) => it.q.length > 0 && it.a.length > 0)
      .slice(0, Math.max(1, Math.min(maxItems, 20))); // hard cap safety
  }, [items, maxItems]);

  const jsonLd = useMemo(() => {
    if (!safeItems.length) return null;

    return {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: safeItems.map((it) => ({
        '@type': 'Question',
        name: it.q,
        acceptedAnswer: {
          '@type': 'Answer',
          text: it.a,
        },
      })),
    };
  }, [safeItems]);

  if (!safeItems.length) return null;

  return (
    <section className="mt-8">
      {/* Visible FAQ (keeps schema honest) */}
      <div className="rounded-3xl bg-white/70 dark:bg-white/10 border border-black/5 dark:border-white/10 shadow-[0_10px_0_rgba(0,0,0,0.06)] p-5">
        <div className="text-lg font-extrabold tracking-tight mb-3">{title}</div>

        <div className="space-y-2">
          {safeItems.map((it, idx) => (
            <details
              key={`${it.q}-${idx}`}
              className="rounded-2xl bg-white/80 dark:bg-white/10 border border-black/5 dark:border-white/10 px-4 py-3"
            >
              <summary className="cursor-pointer font-semibold">
                {it.q}
              </summary>
              <div className="mt-2 text-sm leading-6 opacity-90">
                {it.a}
              </div>
            </details>
          ))}
        </div>
      </div>

      {/* JSON-LD schema (matches visible FAQ 1:1) */}
      {jsonLd ? (
        <script
          type="application/ld+json"
          // JSON.stringify is the correct/standard way to emit JSON-LD safely
          dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }}
        />
      ) : null}
    </section>
  );
}
